import "../sentryInstrument";
import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import * as Sentry from "@sentry/browser";

import ShoppingCartRow from './ShoppingCartRow';
import { updateCartIcons } from './updateCartIcons';
import CheckoutDiscount from './CheckoutDiscount';
import { useCart } from './CartContext';
import ProcessingSpinner from '../components/ProcessingSpinner';
import NoItems from './NoItems';
import StoreHeader from './StoreHeader';

function ShoppingCart() {
  const { 
    setCartSubtotal,
    setDiscountAmount,
    setDiscountLocked,
    setDiscountCode,
    setCartSubtotalWithDiscount,
    rendering,
    setRendering,
   } = useCart();
  const [totalItems, setTotalItems] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [isCheckoutDisabled, setIsCheckoutDisabled] = useState(false);
  const csrftoken = Cookies.get('csrftoken');


  // fetch cart contents on load
  useEffect(() => {
    async function fetchCartData() {
      const response = await fetch("/store/_api/cart/");
      const data = await response.json();
      setCartItems(data.items);
      setCartSubtotal(parseFloat(data.subtotal).toFixed(2));
      setRendering(false);
    }
    fetchCartData();
  }, []);


  // update cart icon in header cart changes
  useEffect(() => {
    if (
      cartItems.some((cartItem) => isNaN(cartItem.quantity)) ||
      cartItems.some((cartItem) => parseInt(cartItem.quantity) <= 0)
    ) {
      console.log("don't update cart because input field is empty string or <= 0");
    } else {
      let itemsCount = 0;
      let subTotalCount = 0;
      cartItems.map((item) => {
        itemsCount += parseInt(item.quantity);
        subTotalCount += parseFloat(item.subtotal);
      });
      setTotalItems(itemsCount);
      setCartSubtotal(subTotalCount.toFixed(2));
      updateCartIcons(itemsCount);
    }

  }, [cartItems]);


  async function deleteQuantity(id) {
    
    // sets animation before deletion
    setCartItems((prevCartItems) => 
      prevCartItems.map((item) => 
        id === item.id ? {...item, itemClass: "cart-row fade-element"} : item
      )
    );

    setTimeout(async () => {        
      const newCart = cartItems.filter(item => item.id !== id);
      setCartItems(newCart);
      try {
        const response = await fetch(`/store/_api/cart/${id}/`, {
          method: "DELETE",
          headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
        });
        const data = await response.json();
        updateCartIcons(data.count);

        if (data.discount_amount === 0) {
          setDiscountCode('');
          setDiscountAmount(0);
          setDiscountLocked(false);
        } else {
          setDiscountAmount(parseFloat(data.discount_amount).toFixed(2));
        }
        setTotalItems(parseInt(data.count));
        setCartSubtotal(parseFloat(data.subtotal).toFixed(2));
        setCartSubtotalWithDiscount(parseInt(data.discounted_subtotal).toFixed(2));


      } catch (error) {
        Sentry.captureException(error);  
      }
    }, 1000);
    
  }  


  async function quantityChange(productId, newQuantity, minQuantity, maxQuantity) {
    const newQty = newQuantity < minQuantity ? minQuantity : newQuantity;
    
    // save for possible need later if API fails
    // FIXME - is this needed?
    const originalCartItems = [...cartItems];  
    
    // handles presentation and makes feel snappier
    const optimisticNewCart = cartItems.map((item) => {
      if (item.id === productId) {
        return {
          ...item,
          quantity: newQty,
          subtotal: newQty * parseFloat(item.price).toFixed(2),
        }
      } else {
        return item;
      }
    });
    setCartItems(optimisticNewCart);

    // actual API call to update cart data in session
    try {
      const response = await fetch(`/store/_api/cart/${productId}/quantity/`, {
		    method: "PUT",
		    headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
		    body: JSON.stringify({quantity: newQty}),
	    });

      if(!response.ok) {
        throw new Error(`API update failed to update a product id: ${productId}`);
      }

      const data = await response.json();
      setCartItems(data.items);

      // handle all other cart data
      if (data.discount_amount === 0) {
        setDiscountCode('')
        setDiscountAmount(0);
      } else {
        setDiscountAmount(parseFloat(data.discount_amount).toFixed(2));
      }
      setTotalItems(parseInt(data.count));
      setCartSubtotal(parseFloat(data.subtotal).toFixed(2));
      setCartSubtotalWithDiscount(parseInt(data.discounted_subtotal).toFixed(2));

    } catch (error) {
      // update cart with errors if api update fails
      Sentry.captureException(error);
      const errorCartItems = optimisticNewCart.map((item) => {
        if (item.id === productId) {
          return {
            ...item,
            error: error.quantity,
          };
        } else {
          return item;
        }
      });
      setCartItems(errorCartItems);
    }


  }


  function handleQuantityInputChange(productId, newQty, min, max) {
    
    newQty = parseInt(newQty);
    if (newQty >= min && newQty <= max ) {
      
      const newCart = cartItems.map((cartItem) => {
        if (cartItem.productDetails.id === productId) {
          return {
            ...cartItem,
            quantity: newQty,
            subtotal: newQty * cartItem.price,
            error: '',
          }
        } else {
          return cartItem;
        }
      });
      setIsCheckoutDisabled(false);
      setCartItems(newCart);

    } else {
      const newCart = cartItems.map((cartItem) => {
        if (cartItem.productDetails.id === productId) {
          return {
            ...cartItem,
            quantity: newQty,
            error: `Quantity must be between ${min} and ${max}`
          };
        }
        return cartItem;
      });
      setIsCheckoutDisabled(true);
      setCartItems(newCart);
      
    }
  
  }


  if (rendering) {
    return(
      <>
        <ProcessingSpinner style={{margin: "5rem auto 4rem",}} />
      </>
    );
  }


  if (totalItems === 0) {
    return(
      <>
        <StoreHeader 
          h1Text={window.gettext("Cart")}
          totalItems={totalItems} 
          headerType="cart"
        />
        <NoItems />
      </>
    );
  }


  return (
    <>
      <StoreHeader 
        h1Text={window.gettext("Cart")}
        totalItems={totalItems} 
        headerType="cart"
      />
      <div className="cart-body">
        <div className="container">
          <div className="cart-rows">

              { cartItems.map((item) => (
                <ShoppingCartRow
                  itemClass={item.itemClass}
                  key={item.id}
                  id={item.id}
                  product={item.productDetails.title}
                  productId={item.productDetails.id}
                  price={parseFloat(item.price).toFixed(2)}
                  fullprice={parseFloat(item.fullprice).toFixed(2)}
                  quantity={item.quantity}
                  image={item.productDetails.image}
                  min={item.min_order_quantity}
                  max={item.max_order_quantity}
                  slug={item.productDetails.slug}
                  thumbnail={item.productDetails.thumbnail}
                  webpThumbnail={item.productDetails.webpThumbnail}
                  handleQuantityInputChange={(e) => {
                    const newQty = parseInt(Math.abs(e.target.value));
                    handleQuantityInputChange(item.productDetails.id, newQty, item.min_order_quantity, item.max_order_quantity);
                  }}
                  handleOnBlur={(e) => {
                    if (!isCheckoutDisabled) {
                      const newQty = parseInt(Math.abs(e.target.value));
                      quantityChange(item.id, newQty, item.min_order_quantity, item.max_order_quantity);
                    }
                  }}
                  handleOnKeyUp={(e) => {
                    if (e.key === "Enter") {
                      const newQty = parseInt(Math.abs(e.target.value));
                      quantityChange(item.id, newQty, item.min_order_quantity, item.max_order_quantity);
                    }
                  }}
                  handleQuantityDecrease={() => quantityChange(
                    item.id, 
                    item.quantity - 1, 
                    item.min_order_quantity,
                    item.max_order_quantity,
                  )}
                  handleQuantityIncrease={() => quantityChange(
                    item.id, 
                    item.quantity + 1, 
                    item.min_order_quantity,
                    item.max_order_quantity,
                  )}
                  handleRemoveProduct={() => deleteQuantity(item.id)}
                  error={item.error}
                />
              ))}

          </div>
          <div className="cart-footer">
              <CheckoutDiscount />

                <div className="ctas">
                  <a href={window.gettext("/store/")} className="btn btn-cart-secondary">{window.gettext("Continue Shopping")}</a>
                  {isCheckoutDisabled === true ? 
                    <div className="btn disabled">{window.gettext("Checkout Disabled")}</div> :
                    <a href={window.gettext("/store/checkout/")} className="btn btn-primary btn-spotlight">{window.gettext("Proceed to Checkout")}</a>
                  }
                </div>
              </div>
        </div>
      </div>
      <div className="container narrow">
        <p 
          className="church-msg balance text-center"
          dangerouslySetInnerHTML={{
            __html: window.gettext(`<strong>Purchasing on behalf of a church or tax-exempt organization?</strong><br />Please contact Ministry Services at <a href="tel:1-800-569-5959" title="Call Revive Our Hearts">1-800-569-5959</a> before placing your order.`)
          }}
        ></p>
      </div>
    </>
  );
}

export default ShoppingCart;
